import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../utils/api';
import LoadingSpinner from '../components/LoadingSpinner';
import Button from '../components/Button';
import Modal from '../components/Modal';
import Input from '../components/Input';
import Select from '../components/Select';
import StatusBadge from '../components/StatusBadge';
import { Plus, RefreshCw } from 'lucide-react';
import { formatRelativeTime, generateRandomPassword, isValidEmail } from '../utils/helpers';

const UsersPage = () => {
  const navigate = useNavigate();
  const { user, workspace } = useAuth();
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    fullName: '',
    role: 'user',
    password: '',
  });

  useEffect(() => {
    if (workspace || user?.role === 'master_admin') {
      loadUsers();
    }
  }, [workspace, user]);

  const loadUsers = async () => {
    try {
      setLoading(true);
      let workspaceId = workspace?.id;
      
      // If master_admin and no workspace, use first workspace or show message
      if (user?.role === 'master_admin' && !workspaceId) {
        const workspaces = await api.get('/workspaces');
        if (workspaces.length > 0) {
          workspaceId = workspaces[0].id;
        } else {
          setUsers([]);
          setLoading(false);
          return;
        }
      }
      
      if (workspaceId) {
        const data = await api.get(`/workspaces/${workspaceId}/users`);
        setUsers(data);
      }
    } catch (error) {
      console.error('Error loading users:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!isValidEmail(formData.email)) {
      alert('Email inválido');
      return;
    }
    
    let workspaceId = workspace?.id;
    if (user?.role === 'master_admin' && !workspaceId) {
      const workspaces = await api.get('/workspaces');
      if (workspaces.length > 0) {
        workspaceId = workspaces[0].id;
      } else {
        alert('No hay workspaces disponibles');
        return;
      }
    }
    
    try {
      await api.post(`/workspaces/${workspaceId}/users`, formData);
      setIsModalOpen(false);
      setFormData({ email: '', fullName: '', role: 'user', password: '' });
      loadUsers();
    } catch (error) {
      console.error('Error creating user:', error);
      alert(error.error || 'Error al crear usuario');
    }
  };

  if (loading) return <LoadingSpinner fullPage />;

  const canCreate = user?.role === 'master_admin' || user?.role === 'client_admin';

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-astra-secondary-500">
          {workspace ? `Usuarios - ${workspace.name}` : 'Usuarios'}
        </h1>
        {canCreate && (
          <Button onClick={() => setIsModalOpen(true)} icon={Plus}>
            Agregar Usuario
          </Button>
        )}
      </div>

      {!workspace && user?.role === 'master_admin' && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <p className="text-sm text-yellow-800">
            Como master_admin, selecciona un workspace para ver sus usuarios o ve a Workspaces para gestionarlos.
          </p>
        </div>
      )}

      <div className="bg-white rounded-xl shadow-lg overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-astra-secondary-500 uppercase">Nombre</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-astra-secondary-500 uppercase">Email</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-astra-secondary-500 uppercase">Rol</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-astra-secondary-500 uppercase">Estado</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-astra-secondary-500 uppercase">Último Login</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {users.length === 0 ? (
              <tr>
                <td colSpan="5" className="px-6 py-4 text-center text-astra-accent-400">
                  No hay usuarios
                </td>
              </tr>
            ) : (
              users.map((u) => (
                <tr key={u.id}>
                  <td className="px-6 py-4 text-sm text-astra-secondary-500">{u.full_name}</td>
                  <td className="px-6 py-4 text-sm text-astra-accent-500">{u.email}</td>
                  <td className="px-6 py-4">
                    <StatusBadge status={u.role === 'client_admin' ? 'active' : 'active'} />
                  </td>
                  <td className="px-6 py-4">
                    <StatusBadge status={u.is_active ? 'active' : 'archived'} />
                  </td>
                  <td className="px-6 py-4 text-sm text-astra-accent-400">
                    {formatRelativeTime(u.last_login)}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title="Agregar Usuario"
      >
        <form onSubmit={handleCreate} className="space-y-4">
          <Input
            label="Email"
            type="email"
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            required
          />
          <Input
            label="Nombre Completo"
            value={formData.fullName}
            onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
            required
          />
          <Select
            label="Rol"
            value={formData.role}
            onChange={(e) => setFormData({ ...formData, role: e.target.value })}
            options={[
              { value: 'user', label: 'Usuario' },
              { value: 'client_admin', label: 'Administrador' },
            ]}
          />
          <div>
            <Input
              label="Contraseña"
              type="text"
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              required
              minLength={8}
            />
            <Button
              type="button"
              variant="ghost"
              onClick={() =>
                setFormData({ ...formData, password: generateRandomPassword() })
              }
              icon={RefreshCw}
              className="mt-2"
            >
              Generar Contraseña Aleatoria
            </Button>
          </div>
          <div className="flex justify-end space-x-4">
            <Button
              type="button"
              variant="secondary"
              onClick={() => setIsModalOpen(false)}
            >
              Cancelar
            </Button>
            <Button type="submit">Crear Usuario</Button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default UsersPage;
